import { Pipe, PipeTransform } from '@angular/core';

/**
 * ScorePtsPipe (followup KAN-225)
 *
 * Centralizes the "earned / total pts" badge formatting used across instructor
 * and learner views (grade book, progress viewer, course summary, split-screen
 * player, attempt selector, etc.).
 *
 * Rules:
 *   - Up to 2 decimal places, trailing zeros trimmed.
 *     - 9        -> "9"
 *     - 8.5      -> "8.5"
 *     - 0.333... -> "0.33"  (round-half-up)
 *   - Integers render with no decimal point ("10/10 pts", not "10.00/10.00 pts").
 *   - Negative scores render with a leading "-" (no Unicode minus weirdness).
 *   - Null/undefined earned or total renders as "—/— pts" rather than "NaN"
 *     so the UI doesn't display broken values.
 *   - Zero total still renders (e.g. "0/0 pts") — no division involved here,
 *     so no NaN risk.
 *
 * Usage:
 *   {{ module.earnedPoints | scorePts:module.totalPoints }}
 *   {{ autoGradedScore | scorePts:autoGradedMaxScore }}
 *
 * The pipe always appends " pts" — callers should NOT add their own suffix.
 */
@Pipe({
  name: 'scorePts',
  standalone: true,
  pure: true,
})
export class ScorePtsPipe implements PipeTransform {
  transform(earned: number | null | undefined, total: number | null | undefined): string {
    // KAN-279: a 0-point quiz (survey / acknowledgement) can never have earned
    // points. Defensively clamp earned to 0 when total is exactly 0 so an
    // already-persisted bad override (earned=100, total=0) can never render the
    // impossible "100/0 pts". The root-cause fix lives in the override handler.
    const safeEarned = total === 0 ? 0 : earned;
    const earnedStr = formatScore(safeEarned);
    const totalStr = formatScore(total);
    return `${earnedStr}/${totalStr} pts`;
  }
}

/**
 * Format a single score value:
 *   - null / undefined / NaN  -> "—"
 *   - integer                 -> no decimal point
 *   - fractional              -> up to 2 decimals, trailing zeros trimmed,
 *                                round-half-up
 *
 * Exported for reuse by score-display.utils (KAN-127 Phase 0).
 */
export function formatScore(value: number | null | undefined): string {
  if (value === null || value === undefined || Number.isNaN(value)) {
    return '—';
  }

  // Round-half-up to 2 decimals. JS's toFixed is round-half-to-even on some
  // engines — use explicit rounding to keep behavior deterministic.
  const rounded = roundHalfUp(value, 2);

  if (Number.isInteger(rounded)) {
    return rounded.toString();
  }

  // Format with up to 2 decimals, strip trailing zeros only after the decimal
  // point. "8.50" -> "8.5", "8.00" -> "8" (caught above as integer),
  // "0.10" -> "0.1", "0.33" -> "0.33".
  return rounded
    .toFixed(2)
    .replace(/(\.\d*?)0+$/, '$1')  // 8.50 -> 8.5, 8.00 -> 8.
    .replace(/\.$/, '');           // 8. -> 8 (defense-in-depth; integer branch covers this)
}

/**
 * Round-half-up. Works for negative numbers the way humans expect:
 *   roundHalfUp(0.335, 2)  -> 0.34
 *   roundHalfUp(-0.335, 2) -> -0.33  (away-from-zero would give -0.34;
 *                                     "round half up" treats -0.335 as
 *                                     halfway between -0.34 and -0.33,
 *                                     and "up" means toward +infinity)
 */
function roundHalfUp(value: number, decimals: number): number {
  const factor = Math.pow(10, decimals);
  return Math.floor(value * factor + 0.5) / factor;
}
